import React from 'react';
import { Card, Col, Placeholder, Row } from 'react-bootstrap';
import CardLayout from '../../../../shared/components/card/CardLayout';

const SectionHeader = ({ className = 'mb-2' }: { className?: string }) => (
    <div className={`d-flex align-items-center ${className}`}>
        <Placeholder className="skeleton skeleton-header-icon mr-2" />
        <Placeholder className="skeleton skeleton-table-cell skeleton-table-cell-wide" />
    </div>
);

const Stat = () => (
    <div>
        <Placeholder className="skeleton skeleton-stat-value d-block mb-1" />
        <Placeholder className="skeleton skeleton-stat-label d-block" />
    </div>
);

const InlineStat = () => (
    <div className="d-flex align-items-end">
        <Placeholder className="skeleton skeleton-number" />
        <Placeholder className="skeleton skeleton-table-cell ml-1" />
    </div>
);

const StatSection = ({ className = '', solidButton = false }: { className?: string; solidButton?: boolean }) => (
    <div className={`d-flex flex-column justify-content-between h-100 ${className}`}>
        <SectionHeader />
        <div className="d-flex align-items-center justify-content-between mt-1">
            <Stat />
            <Placeholder
                className={`skeleton ${solidButton ? 'skeleton-action-btn' : 'skeleton-outline-btn'}`}
                style={{ width: 90, height: 38 }}
            />
        </div>
    </div>
);

const ActionIcons = () => (
    <div className="d-flex align-items-center justify-content-between">
        {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="d-flex flex-column align-items-center">
                <Placeholder className="skeleton skeleton-header-icon" />
                <Placeholder className="skeleton skeleton-table-cell skeleton-table-cell-sm mt-1" />
            </div>
        ))}
    </div>
);

const InfoBlock = () => (
    <div className="info-block">
        <Placeholder className="skeleton skeleton-table-cell skeleton-table-cell-wide d-block mb-1" />
        <Placeholder className="skeleton skeleton-stat-label d-block" />
    </div>
);

const ProgressBlock = () => (
    <>
        <div className="d-flex justify-content-between mb-2">
            <Placeholder className="skeleton skeleton-table-name" />
            <Placeholder className="skeleton skeleton-table-cell skeleton-table-cell-sm" />
        </div>
        <Placeholder className="skeleton skeleton-progress d-block" />
    </>
);

export function ExploreOffersCardSkeleton() {
    return (
        <CardLayout>
            <Row className="align-items-center h-100 placeholder-glow" aria-hidden="true">
                <StatSection className="col-6 border-right" solidButton />
                <StatSection className="col-6" />
            </Row>
        </CardLayout>
    );
}

export function PlannerReportsCardSkeleton() {
    return (
        <CardLayout>
            <Row className="align-items-start h-100 placeholder-glow" aria-hidden="true">
                <StatSection className="col-4 border-right" />
                <StatSection className="col-4 border-right" />
                <StatSection className="col-4" />
            </Row>
        </CardLayout>
    );
}

export function InvoiceCardSkeleton() {
    return (
        <CardLayout classCard="h-100">
            <div className="placeholder-glow h-100" aria-hidden="true">
                <StatSection className="pr-0" />
            </div>
        </CardLayout>
    );
}

export function MyOffersSkeleton() {
    return (
        <div className="placeholder-glow" aria-hidden="true">
            <Row>
                <Col xs={8} className="pr-1">
                    <CardLayout classBody="px-2" classCard="border-dashed">
                        <SectionHeader className="" />
                        <div className="mt-3 d-flex align-items-center" style={{ gap: 8 }}>
                            <InlineStat />
                            <InlineStat />
                        </div>
                    </CardLayout>
                </Col>
                <Col className="col-4 pl-1">
                    <CardLayout classBody="px-2" classCard="h-100 border-dashed">
                        <SectionHeader className="" />
                        <div className="mt-3">
                            <InlineStat />
                        </div>
                    </CardLayout>
                </Col>
            </Row>
            <div className="d-flex align-items-stretch justify-content-between mt-3 mb-4">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className={`card border-dashed p-3 flex-grow-1 ${i === 1 ? 'mx-2' : ''}`}>
                        <SectionHeader className="" />
                        <Placeholder className="skeleton skeleton-number d-block mt-2" />
                    </div>
                ))}
            </div>
        </div>
    );
}

function ListRowSkeleton({ infoBlocks, lastBlock }: { infoBlocks: number; lastBlock: 'progress' | 'actions' }) {
    return (
        <CardLayout classBody="contract-list-card px-3 py-0 placeholder-glow">
            <div className="image-wrapper">
                <Placeholder className="skeleton skeleton-fill" />
            </div>
            <div className="card-content pl-3">
                <Placeholder className="skeleton skeleton-table-name d-block" />
                <Placeholder className="skeleton skeleton-stat-label d-block mt-2" />
            </div>
            <div className="card-info">
                {Array.from({ length: infoBlocks }).map((_, i) => (
                    <InfoBlock key={i} />
                ))}
                <div className="info-block">{lastBlock === 'progress' ? <ProgressBlock /> : <ActionIcons />}</div>
            </div>
        </CardLayout>
    );
}

function ContractMobileCardSkeleton() {
    return (
        <Card className="p-2 h-100 d-flex flex-column justify-content-between overflow-hidden placeholder-glow">
            <div className="d-flex align-items-start justify-content-between">
                <div className="w-50">
                    <Placeholder className="skeleton skeleton-offer-image" />
                </div>
                <div className="w-50 px-3 d-flex flex-column">
                    <Placeholder xs={10} className="skeleton skeleton-title d-block mb-3" />
                    <Placeholder className="skeleton skeleton-stat-label d-block mb-1" />
                    <Placeholder className="skeleton skeleton-table-cell skeleton-table-cell-wide d-block mb-2" />
                    <Placeholder className="skeleton skeleton-table-cell d-block" />
                </div>
            </div>
            <div className="d-flex mt-3">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="col-4 p-0 d-flex flex-column align-items-center">
                        <Placeholder className="skeleton skeleton-table-cell" />
                        <Placeholder className="skeleton skeleton-stat-label mt-1" />
                    </div>
                ))}
            </div>
            <div className="mb-2 mt-3">
                <ProgressBlock />
            </div>
        </Card>
    );
}

export function CardViewOfferSkeleton() {
    return (
        <Card className="p-3 h-100 d-flex flex-column justify-content-between overflow-hidden placeholder-glow">
            <div className="d-flex align-items-start justify-content-between">
                <div className="w-40">
                    <Placeholder className="skeleton skeleton-offer-image" />
                    <div className="mt-3">
                        <ActionIcons />
                    </div>
                </div>
                <div className="w-60 pl-md-3 pl-2 d-flex flex-column">
                    <Placeholder xs={10} className="skeleton skeleton-title d-block" />
                    <Placeholder xs={6} className="skeleton d-block mt-2" />
                    <Placeholder className="skeleton skeleton-stat-label d-block mt-3" />
                    <Placeholder xs={10} className="skeleton d-block mt-1" />
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="d-flex justify-content-between mt-2">
                            <Placeholder className="skeleton skeleton-stat-label" />
                            <Placeholder className="skeleton skeleton-table-cell" />
                        </div>
                    ))}
                </div>
            </div>
        </Card>
    );
}

export function ContractListSkeleton({ count = 2 }: { count?: number }) {
    return (
        <div className="my-2" aria-hidden="true">
            <div className="placeholder-glow mb-lg-4">
                <Placeholder className="skeleton skeleton-section-title" />
            </div>
            <Row>
                {Array.from({ length: count }).map((_, i) => (
                    <Col key={i} className="my-2 col-12 mb-3">
                        <div className="d-lg-none">
                            <ContractMobileCardSkeleton />
                        </div>
                        <div className="d-none d-lg-block">
                            <ListRowSkeleton infoBlocks={4} lastBlock="progress" />
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );
}

export function OfferListSkeleton({ isCardView }: { isCardView: boolean }) {
    return (
        <div className="pt-3" aria-hidden="true">
            <Row>
                {isCardView
                    ? Array.from({ length: 6 }).map((_, i) => (
                          <Col key={i} className="my-2 col-12 col-lg-6 col-xl-4">
                              <CardViewOfferSkeleton />
                          </Col>
                      ))
                    : Array.from({ length: 4 }).map((_, i) => (
                          <Col key={i} className="col-12 mb-4 pb-2">
                              <ListRowSkeleton infoBlocks={4} lastBlock="actions" />
                          </Col>
                      ))}
            </Row>
        </div>
    );
}
